import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import BookingService, { BookingProps } from 'services/hooks/booking-service';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import { format, roundToNearestMinutes } from 'date-fns';
import { Table } from '../Common/Table';
import Delete from './BookingForms/delete';
import Save from './BookingForms/save';
import { FilterSelect } from 'utils/FilterSelectUtils';
import ResourceService, { ResourceProps } from 'services/hooks/resource-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import { utcToZonedTime } from 'date-fns-tz';

export default function BookingSaveMode() {
  const [getBookingsResult, setGetBookingsResult] = useState<BookingProps[] | null>([]);
  const [buildingList, setBuildingList] = useState<BuildingProps[] | null>();
  const [resourceList, setresourceList] = useState<ResourceProps[] | null>();

  const {
    isLoading: isLoadingBookings,
    refetch,
    data: bookingData
  } = useQuery<BookingProps[], Error>('bookings', BookingService.findAllBookings, {
    onError: (err: any) => {
      console.log(err.response?.data || err);
    }
  });

  const { refetch: buildingRefetch, data: buildingData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  const { refetch: resourceRefetch, data: resourceData } = useQuery<ResourceProps[], Error>(
    'resources',
    ResourceService.findAllResources,
    {
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  useEffect(() => {
    if (bookingData && Array.isArray(bookingData)) {
      // Sort bookings according to start date
      bookingData?.sort(function (a, b) {
        return new Date(a.start).valueOf() - new Date(b.start).valueOf();
      });

      setGetBookingsResult(bookingData);
    }

    if (resourceData && Array.isArray(resourceData)) {
      resourceRefetch();
      setresourceList(resourceData);
    }

    if (buildingData && Array.isArray(buildingData)) {
      buildingRefetch();
      setBuildingList(buildingData);
    }
  }, [bookingData, resourceData, resourceRefetch, buildingData, buildingRefetch]);

  const shortText = (text: string, count: number) =>
    text?.slice(0, count) + (text?.length > count ? '...' : '');

  const columns = React.useMemo(
    () => [
      {
        id: 'building',
        Header: 'BUILDING',
        accessor: (row: { buildingUuid: string }) => {
          return buildingList?.find((f: { uuid?: string }) => f.uuid === row.buildingUuid)?.name;
        },
        Filter: FilterSelect
      },
      {
        id: 'bookedBy',
        Header: 'BOOKED BY',
        accessor: (row: {
          MobileUser: { firstName: any; lastName: any };
          createdAt: string;
          updatedAt: string;
          outlookBooking?: boolean;
          outlookOrganizer?: string;
        }) => {
          return row.outlookBooking
            ? row.outlookOrganizer
            : row.MobileUser.firstName + ' ' + row.MobileUser.lastName;
        },
        disableFilters: true
      },
      {
        id: 'reason',
        Header: 'REASON',
        accessor: (row: { reason: any }) => row.reason && shortText(row.reason, 50),
        disableFilters: true
      },
      {
        id: 'resourceName',
        Header: 'RESOURCE',
        accessor: (row: { resource: string }) => {
          return resourceList?.find((resource) => resource.uuid === row.resource)?.name;
        },
        disableFilters: true
      },
      {
        id: 'date',
        Header: 'DATE',
        accessor: (row: { start: string; buildingTimezone: string }) => {
          const timeZone = row.buildingTimezone;

          // Convert start and end date and time to Date format (in provided timezone)
          const startDate = utcToZonedTime(new Date(row.start), timeZone);

          // Below code is formatting the `startDate` variable to a string representation of the date in the format "MM/DD/YYYY".
          const formattedDate = format(startDate, 'PP');

          return `${formattedDate}`;
        },
        disableFilters: true
      },
      {
        id: 'time',
        Header: 'TIME',
        accessor: (row: { start: string; end: string; buildingTimezone: string }) => {
          const timeZone = row.buildingTimezone;

          // Convert start and end date and time to Date format (in provided timezone)
          const startDate = utcToZonedTime(new Date(row.start), timeZone);
          const endDate = utcToZonedTime(new Date(row.end), timeZone);

          //Below code is formatting the `startDate` variable to a string representation of the time in the format "h:mm a".
          const formattedStartTime = format(startDate, 'p');
          // Below code is formatting the `endDate` variable to a string representation of the time in the format "h:mm a".
          const formattedEndTime = format(roundToNearestMinutes(endDate, { nearestTo: 15 }), 'p');

          return `${formattedStartTime} - ${formattedEndTime}`;
        },
        disableFilters: true
      },
      {
        id: 'actions',
        Header: 'ACTIONS',
        accessor: (data: any) => data,
        Cell: (row: any) => {
          // Convert start and end date and time to Date format (in provided timezone)
          const startDate = utcToZonedTime(new Date(row.value.start), row.value.buildingTimezone);
          const endDate = utcToZonedTime(new Date(row.value.end), row.value.buildingTimezone);

          // Get current date and time in the building's timezone
          const currentTimeInTimezone = utcToZonedTime(new Date(), row.value.buildingTimezone);

          // Check if either startDate or endDate are in the past relative to the building's timezone
          const isOngoingOrPastBooking =
            startDate < currentTimeInTimezone || endDate < currentTimeInTimezone;

          return (
            <div className="flex gap-4">
              {!isOngoingOrPastBooking && row.value.outlookBooking === false && (
                <SecondaryButton
                  onClick={() => {
                    row.setEditUUID(row.value.uuid);
                    row.setUpdateShowModal(true);
                  }}
                  buttonLabel="Edit"
                  variant="default"
                  size="px-4 w-auto"
                />
              )}
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setResourceUUID(row.value.resource);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    [resourceList, buildingList]
  );

  if (isLoadingBookings || !getBookingsResult) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return !isLoadingBookings && getBookingsResult ? (
    <div className="booking">
      <Table
        name="Bookings"
        refetch={refetch}
        columns={columns}
        data={getBookingsResult}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={BookingService.createBooking}
        updateQuery={BookingService.updateBooking}
        deleteQuery={BookingService.deleteBookingById}
        editQuery={BookingService.findBookingById}
        queryName="bookings"
        buttonName="Booking"
        toolTipMessage="Handle bookings for resources."
      />
    </div>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
}
